import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./Hero.css";
import dark_arrow from "../assets/dark-arrow.png";
import heroImage from "../assets/indian3.png";

import chandruVideo from "../assets/videos/chandru.mp4";
import captianVideo from "../assets/videos/captain.mp4";
import rakeshVideo from "../assets/videos/rakesh.mp4";
import umaVideo from "../assets/videos/uma.mp4";

import chandruThumb from "../assets/chandrutumb.jpg";
import captainThumb from "../assets/captaintumb.jpg";
import rakeshThumb from "../assets/rakeshtumb.jpg";
import umaThumb from "../assets/nalini.jpg";

const slides = [
  {
    id: 1,
    type: "image",
    heading: (
      <>
        Welcome to <br />
        <span>MANDI HARIYANNA <br/> Academy</span>
      </>
    ),
    subText:
      "Where ambition meets opportunity. We turn raw potential into professional excellence through practical, industry-driven learning.",
    infoText:
      "More than just an institution, MANDI HARIYANNA Academy is your career launchpad. We bridge the gap between academic theory and industry reality, equipping you with the cutting-edge skills and confidence needed to dominate the competitive job market.",
    buttonText: "Unlock Your Potential",
    image: heroImage,
  },
  {
    id: 2,
    type: "video",
    heading: "Transform Your Future",
    subText:
      "Mukhyamantri Chandru Talking about Mandi Hariyanna Academy, we believe in practical learning combined with industry expertise. Mukiya Mandhrichandru personally mentors every program, ensuring you gain the knowledge and confidence to excel in today’s competitive job market. Whether you're looking to upskill, switch careers, or jumpstart your professional growth, our courses are tailored to help you achieve your goals.",
    buttonText: "Start Learning",
    video: chandruVideo,
    thumbnail: chandruThumb,
  },
  {
    id: 3,
    type: "video",
    heading: "Train with Real Tools",
    subText:
      "Captain Harish at Mandi Hariyanna Academy, we emphasize hands-on training backed by real-world experience. Captain Harish personally oversees each program, ensuring you develop the skills and confidence needed to succeed in today’s fast-evolving job market. Whether you want to upgrade your expertise, explore new career paths, or accelerate your professional journey, our tailored courses are designed to help you reach your ambitions.",
    buttonText: "Explore Programs",
    video: captianVideo,
    thumbnail: captainThumb,
  },
  {
    id: 4,
    type: "video",
    heading: "Get Certified. Get Hired.",
    subText:
      "CEO Rakesh at Mandi Hariyanna Academy, leadership and innovation drive our success. CEO Rakesh leads by example, guiding each program to equip you with cutting-edge skills and industry insights. Whether you're aiming to lead teams, innovate in your field, or elevate your career to new heights, our carefully crafted courses empower you to become a confident and visionary professional.",
    buttonText: "View Certifications",
    video: rakeshVideo,
    thumbnail: rakeshThumb,
  },
  {
    id: 5,
    type: "video",
    heading: "Empower Through Education",
    subText:
      "Dr. Uma Gautham at Mandi Hariyanna Academy, we focus on transforming lives through education and career empowerment. Dr. Uma Gautham emphasizes the importance of skill-based learning and personal growth, ensuring each course is rooted in real-world application. Whether you're preparing for competitive exams or seeking career advancement, her guidance ensures you’re equipped to thrive with confidence and purpose.",
    buttonText: "Discover Courses",
    video: umaVideo,
    thumbnail: umaThumb,
  },
];

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const heroRef = useRef(null);
  const navigate = useNavigate();

  // Auto slide interval
  useEffect(() => {
    if (isPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isPlaying]);

  // Pause video on slide change
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.muted = true;
      videoRef.current.controls = false;
    }
    setIsPlaying(false);
  }, [currentSlide]);

  // Scroll listener to pause video when hero is out of viewport
  useEffect(() => {
    const handleScroll = () => {
      if (!videoRef.current || !heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handlePlayClick = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = false;
    videoRef.current.loop = false;
    videoRef.current.controls = true;
    videoRef.current.controlsList = "nodownload";
    videoRef.current.play();
    setIsPlaying(true);
  };

  return (
    <section className="hero" ref={heroRef}>
      <div className="hero-container">
        {/* Left: Content */}
        <div className="hero-content">
          <motion.h1
            key={currentSlide}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ duration: 0.7 }}
            className="multicolor-text"
          >
            {slides[currentSlide].heading}
          </motion.h1>

          <motion.p
            key={"subtext-" + currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            {slides[currentSlide].subText}
          </motion.p>

          {slides[currentSlide].infoText && (
            <motion.p
              key={"infoText-" + currentSlide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="academy-info"
            >
              {slides[currentSlide].infoText}
            </motion.p>
          )}

          <motion.button
            key={"button-" + currentSlide}
            className="btn"
            onClick={() => navigate("/courses")}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {slides[currentSlide].buttonText}
            <img src={dark_arrow} alt="Arrow icon" />
          </motion.button>
        </div>

        {/* Right: Media */}
        <div className="hero-image" aria-live="polite">
          {slides[currentSlide].type === "image" ? (
            <img
              src={slides[currentSlide].image}
              alt="Hero Slide"
              className={`image-style ${currentSlide === 0 ? "no-background" : ""}`}
              style={{
                filter: currentSlide === 0 ? "brightness(70%)" : "brightness(100%)",
              }}
            />
          ) : (
            <div className="video-wrapper">
              <video
                ref={videoRef}
                src={slides[currentSlide].video}
                poster={slides[currentSlide].thumbnail}
                preload="metadata"
                playsInline
                muted
                controls={isPlaying}
                controlsList="nodownload"
                style={{ display: isPlaying ? "block" : "none" }}
                onEnded={() => setIsPlaying(false)}
              />
              {!isPlaying && (
                <>
                  <button className="video-play-button" onClick={handlePlayClick}>
                    ▶
                  </button>
                  <img
                    src={slides[currentSlide].thumbnail}
                    alt={`Video thumbnail ${currentSlide + 1}`}
                    className="image-style"
                    onClick={handlePlayClick}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Page Indicator BELOW hero-container */}
      <div className="page-indicator">
        {slides.map((slide, index) => (
          <span
            key={slide.id}
            className={`dot ${currentSlide === index ? "active" : ""}`}
            onClick={() => setCurrentSlide(index)}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
